import Box from "@mui/material/Box";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProductCategoryInput } from "../../../../components/filters/ProductCategory/ProductCategoryInput";
import { useIntersectionObserver } from "../../../../components/IntersectionObserver";
import LazyRenderer from "../../../../components/LazyRenderer";
import { useCombinedFilters } from "../../../../hooks/useCombinedFilters";
import { useTopProductsQuery } from "../../../../legacy/hooks/useTopProductsQuery";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { Workspace } from "../../../../types/workspace";
import { softwareProductCategory } from "../../../../utils/consts";
import { KeyPlayerMarketPenetrationBySegment } from "./charts/KeyPlayerMarketPenetrationBySegment/KeyPlayerMarketPenetrationBySegment";
import { KeyPlayerMarketPenetrationChart } from "./charts/KeyPlayerMarketPenetrationChart/KeyPlayerMarketPenetrationChart";
import { KeyPlayerMarketPenetrationTrendChart } from "./KeyPlayerMarketPenetrationTrendChart/KeyPlayerMarketPenetrationTrendChart";
import { KeyPlayerNewProductDetectionsChart } from "./KeyPlayerNewProductDetectionsChart";
import { KeyPlayerProductDetectionsBySegmentChart } from "./KeyPlayerProductDetectionsBySegmentChart";

export function TopProductsSection() {
  const [chartIsVisible, setChartIsVisible] = useState(false);
  const combinedFilters = useCombinedFilters();
  const setWorkspace = useWorkspaceStore((state) => state.setDraft);
  const productCategoryIds = useWorkspaceStore(
    (state) => state.draft?.configuration.productCategoryIds || []
  );
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );

  const { container } = useIntersectionObserver();

  const [tempCategoryIds, setTempCategoryIds] =
    useState<string[]>(productCategoryIds);
  const [selectedCategoryIds, setSelectedCategoryIds] =
    useState<string[]>(productCategoryIds);

  useEffect(() => {
    setTempCategoryIds(productCategoryIds);
    setSelectedCategoryIds(productCategoryIds);
  }, [productCategoryIds]);

  // Create refs to track visibility state for each lazy renderer
  const chartVisibilityRef = useRef(false);

  // Handle visibility change with useCallback to avoid render-time setState
  const handleVisibilityChange = useCallback((isVisible: boolean) => {
    if (isVisible && !chartVisibilityRef.current) {
      chartVisibilityRef.current = true;
      // Update state outside of render cycle using setTimeout
      setTimeout(() => {
        setChartIsVisible(true);
      }, 0);
    }
  }, []);

  //** Selected Product Categories will be applied when user loses focus from the input
  const handleSelectCategoryIds = useCallback(() => {
    // prevent user from having no productCategories applied, auto apply externalIt in that case
    const idsToApply =
      tempCategoryIds.length === 0 ? softwareProductCategory : tempCategoryIds;
    setTempCategoryIds(idsToApply);
    setSelectedCategoryIds(idsToApply);
    setWorkspace(
      (draft) =>
        ({
          ...draft,
          configuration: {
            ...draft?.configuration,
            productCategoryIds: idsToApply,
          },
        }) as Workspace
    );
  }, [tempCategoryIds, setWorkspace]);

  const { isLoadingTopProducts, topProducts: topProductsData } =
    useTopProductsQuery({
      companyFilters: combinedFilters || {},
      productCategoryIds: selectedCategoryIds,
      localizeInstalls,
      skip: !chartIsVisible,
    });

  //* Currently displaying top 5 products, potentially might revert to top 10 in the future
  const top5ProductIds = useMemo<number[]>(() => {
    return [...topProductsData]
      .sort((a, b) => b.companyCount - a.companyCount)
      .slice(0, 5)
      .map((product) => product.productId);
  }, [topProductsData]);

  const top10ProductIds = useMemo<number[]>(() => {
    return [...topProductsData]
      .sort((a, b) => b.companyCount - a.companyCount)
      .slice(0, 10)
      .map((product) => product.productId);
  }, [topProductsData]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="10px"
      width="100%"
      minWidth={0}
      maxWidth="100%"
    >
      <Box display="flex" width="550px" maxWidth="100%">
        <ProductCategoryInput
          id="market-landscape-product-category-select"
          selectedItems={tempCategoryIds}
          onProductCategorySelect={setTempCategoryIds}
          onClose={handleSelectCategoryIds}
        />
      </Box>

      <LazyRenderer
        root={container.current || undefined}
        fallback={<></>}
        threshold={0.1}
        onVisible={handleVisibilityChange}
      >
        <KeyPlayerMarketPenetrationChart
          isLoadingTopProducts={isLoadingTopProducts || !chartIsVisible}
          topProductIds={top10ProductIds}
        />
      </LazyRenderer>

      <Box
        display="flex"
        gap="10px"
        flexDirection={"row"}
        width="100%"
        minWidth={0}
        maxWidth="100%"
      >
        <Box flex={1} minWidth={0} maxWidth="100%">
          <LazyRenderer
            root={container.current || undefined}
            fallback={<></>}
            threshold={0.1}
            onVisible={handleVisibilityChange}
          >
            <KeyPlayerMarketPenetrationTrendChart
              isLoading={isLoadingTopProducts || !chartIsVisible}
              topProductIds={top5ProductIds}
            />
          </LazyRenderer>
        </Box>

        <Box flex={1} minWidth={0} maxWidth="100%">
          <LazyRenderer
            root={container.current || undefined}
            fallback={<></>}
            threshold={0.1}
            onVisible={handleVisibilityChange}
          >
            <KeyPlayerMarketPenetrationBySegment
              isLoading={isLoadingTopProducts || !chartIsVisible}
              topProductIds={top5ProductIds}
            />
          </LazyRenderer>
        </Box>
      </Box>

      {/*
        LazyRenderer removed for these charts due to IntersectionObserver issues.
        The LazyRenderer was not detecting when these charts entered the viewport.
        Since these are the last charts on the page and only render when top products section is visible,
        removing LazyRenderer ensures they always render when the data is available.
      */}
      <Box
        display="flex"
        gap="10px"
        flexDirection="row"
        width="100%"
        minWidth={0}
        maxWidth="100%"
      >
        <Box flex={1} minWidth={0} maxWidth="100%">
          <KeyPlayerNewProductDetectionsChart
            isLoading={isLoadingTopProducts || !chartIsVisible}
            topProductIds={top5ProductIds}
          />
        </Box>
        <Box flex={1} minWidth={0} maxWidth="100%">
          <KeyPlayerProductDetectionsBySegmentChart
            isLoading={isLoadingTopProducts || !chartIsVisible}
            topProductIds={top5ProductIds}
          />
        </Box>
      </Box>
    </Box>
  );
}
